/**
 * Alshallal Pastry Company HR System - Top Navigation Bar
 * User menu, language toggle, and notifications
 * Phase 19: Added expiring documents badge
 * Mobile: Hamburger menu toggle
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Globe, 
  Bell, 
  User, 
  LogOut, 
  Settings,
  ChevronDown,
  Menu
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import ExpiringDocsBadge from '@/components/notifications/ExpiringDocsBadge';
import { ModeSwitcher } from '@/components/auth/ModeSwitcher';

export const Topbar = ({ sidebarCollapsed, isMobile = false, onMobileMenuToggle }) => {
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage, isRTL, getRoleDisplay } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = () => {
    if (!user) return '?';
    const first = user.first_name?.[0] || '';
    const last = user.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const getUserDisplayName = () => {
    if (!user) return '';
    if (isRTL && user.first_name_ar) {
      return `${user.first_name_ar} ${user.last_name_ar || ''}`.trim();
    }
    return `${user.first_name} ${user.last_name}`.trim();
  };

  return (
    <header 
      className={cn(
        "fixed top-0 h-16 brand-glass z-30 transition-all duration-300",
        "flex items-center justify-between px-4 sm:px-6",
        // Mobile: full width, Desktop: offset by sidebar
        isMobile 
          ? "left-0 right-0"
          : sidebarCollapsed 
            ? (isRTL ? "right-16 left-0" : "left-16 right-0")
            : (isRTL ? "right-64 left-0" : "left-64 right-0")
      )}
      data-testid="topbar"
    >
      {/* Left side - Mobile menu toggle + Page title area.
          `min-w-0` matters: without it the company name cannot shrink, so on a
          narrow phone it pushed the mode pill, the bell and the profile menu
          off the right edge instead of truncating itself. */}
      <div className="flex items-center gap-3 min-w-0">
        {/* Mobile hamburger menu */}
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMobileMenuToggle}
            className="flex-shrink-0 hover:bg-brand-beige-60"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}
        
        {/* App name on mobile */}
        {isMobile && (
          <span className="font-semibold text-brand-black text-base truncate">
            {isRTL ? 'معجنات الشلال' : 'Alshallal Pastry'}
          </span>
        )}
      </div>

      {/* Right side - Actions */}
      <div className="flex shrink-0 items-center gap-1 sm:gap-3">
        {/* Which hat you are wearing: Employee, or your own role */}
        <ModeSwitcher />

        {/* Language Toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleLanguage}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 hover:bg-brand-beige-60 transition-all"
          data-testid="language-toggle"
        >
          <Globe className="h-4 w-4" />
          <span className="text-xs sm:text-sm font-medium hidden xs:inline">
            {language === 'en' ? 'العربية' : 'EN'}
          </span>
        </Button>

        {/* Phase 19: Expiring Documents Badge (HR only) */}
        <ExpiringDocsBadge />

        {/* Notifications - Placeholder */}
        <Button
          variant="ghost"
          size="icon"
          className="relative h-8 w-8 sm:h-9 sm:w-9 hover:bg-brand-beige-60"
          data-testid="notifications-btn"
        >
          <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
        </Button>

        {/* Quick Logout Button - Hidden on mobile */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="text-muted-foreground hover:text-brand-brown hover:bg-brand-brown/10 hidden lg:flex items-center gap-2 transition-colors"
          data-testid="quick-logout-btn"
        >
          <LogOut className="h-4 w-4" />
          <span className="text-sm">{t('logout')}</span>
        </Button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="flex items-center gap-1 sm:gap-2 px-1 sm:px-2 hover:bg-brand-beige-60"
              data-testid="user-menu-trigger"
            >
              <Avatar className="h-7 w-7 sm:h-8 sm:w-8 ring-2 ring-brand-orange/30">
                <AvatarFallback className="bg-brand-black text-brand-beige text-xs sm:text-sm font-semibold">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:flex flex-col items-start text-start min-w-0">
                <span className="text-sm font-medium text-brand-black truncate max-w-[160px]" title={getUserDisplayName()}>
                  {getUserDisplayName()}
                </span>
                <span className="text-xs text-brand-brown truncate max-w-[160px]" title={getRoleDisplay(user?.role)}>
                  {getRoleDisplay(user?.role)}
                </span>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground hidden sm:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={isRTL ? "start" : "end"} className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="truncate">{getUserDisplayName()}</span>
                <span className="text-xs text-muted-foreground font-normal truncate">
                  {user?.email}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => navigate('/profile')}
              data-testid="user-menu-profile"
            >
              <User className="h-4 w-4 me-2" />
              {t('profile')}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => navigate('/settings')}
              data-testid="user-menu-settings"
            >
              <Settings className="h-4 w-4 me-2" />
              {t('settings')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout}
              className="text-brand-brown focus:text-brand-brown"
              data-testid="user-menu-logout"
            >
              <LogOut className="h-4 w-4 me-2" />
              {t('logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Topbar;
